import { FieldInstance, FieldInstanceForResolver, FieldTemplate, FormInstance } from "db/definitions";
import { bulkSaveFieldInstances, createFieldForBatchResolver } from "../db/queries/fieldInstanceQueries";
import batchCreateFieldInstances from "./batchCreateFieldInstances";
import { consolidateDuplicateTableInstances, TableConsolidation } from "./consolidateDuplicateTableInstances";
import getTableFields from "./getTableFields";
import { handleBatchCrud } from "./handleBatchCrud";
import { recordRepairBreadcrumb, recordSentry } from "./recordSentry";
import updateTable from "./updateTable";

/**
 * Folder-open counterpart of the merge consolidation: when a form holds more than one instance for the same table or
 * photo log template, fold them into the oldest one right now instead of waiting for the next sync. Same rule as the
 * merge (consolidateDuplicateTableInstances), so the two can never disagree, and the merge re-converges anything a
 * stale device undoes later.
 *
 * It is gated so that a clean folder costs nothing: no duplicates, or no loser with rows or cells → no fetch, no
 * write, no Sentry. It refuses to run on a form where any instance has no template id (a corrupted load; the same
 * state in which planMissingFieldInstances refuses to create anything), and it refuses to touch a group whose loser
 * cells could not all be fetched, because re-pointing a partial cell set would strand the rest.
 *
 * Writes go cells → survivor → loser. If the process dies part way, every intermediate state is one the next open
 * or sync completes: re-pointed cells without the survivor's new rows are orphans the table repair adopts, and a
 * survivor with the rows but a loser that still has them is exactly the "stale device" state the rule re-converges.
 *
 * @param formInstance The loaded form; its top-level instances are mutated in place so the render shows the result
 * @param fieldTemplates The form template's field templates (for inputType)
 * @param formGroupId For the breadcrumb
 * @param online Which database to write to
 * @returns One entry per group that was consolidated
 */
export async function consolidateTableInstancesOnOpen(
	formInstance: FormInstance,
	fieldTemplates: FieldTemplate[],
	formGroupId: string,
	online: boolean,
): Promise<TableConsolidation[]> {
	const instances = (formInstance.fieldInstances?.items ?? []) as FieldInstance[];
	if (instances.length < 2) return [];

	const templateById = new Map<string, FieldTemplate>();
	for (const fieldTemplate of fieldTemplates) templateById.set(fieldTemplate.id, fieldTemplate);
	const isTableTemplate = (fieldTemplateId: string): boolean | undefined => {
		const fieldTemplate = templateById.get(fieldTemplateId);
		if (!fieldTemplate) return undefined;
		return fieldTemplate.inputType === "table" || fieldTemplate.inputType === "imageroll";
	};

	// Gate 1 + 2: find table groups with more than one instance; refuse the whole form if any instance has no template id.
	const groups = new Map<string, FieldInstance[]>();
	for (const instance of instances) {
		const templateId = instance.fieldTemplate?.id || instance.fieldTemplateId;
		if (!templateId) return [];
		if (!isTableTemplate(templateId)) continue;
		const group = groups.get(templateId);
		if (group) group.push(instance);
		else groups.set(templateId, [instance]);
	}
	const duplicateGroups = Array.from(groups.values()).filter((group) => group.length > 1);
	if (duplicateGroups.length === 0) return [];

	const done: TableConsolidation[] = [];
	for (const group of duplicateGroups) {
		const ordered = [...group].sort((a, b) => (a.createdAt || "").localeCompare(b.createdAt || "") || a.id.localeCompare(b.id));
		const survivor = ordered[0];
		const losers = ordered.slice(1);

		// Gate 3: every loser cell, all pages, or nothing.
		const loserCells: FieldInstance[] = [];
		try {
			for (const loser of losers) {
				const cells = await getTableFields(loser.id, online, { throwOnError: true });
				loserCells.push(...cells);
			}
		} catch (error) {
			recordSentry({
				error,
				msg: "Open-time consolidation skipped a table: loser cells could not be fetched",
				additionalData: { formGroupId, formInstanceId: formInstance.id, survivorId: survivor.id, loserIds: losers.map((loser) => loser.id), online },
			});
			continue;
		}

		// The helper only reads id/createdAt/formInstance/fieldTemplateId/tableField/fieldsOrder/authors. Top-level
		// instances loaded from the cloud carry formInstance as a string id.
		const candidates: (FieldInstance | FieldInstanceForResolver)[] = [survivor, ...losers, ...loserCells];
		const result = consolidateDuplicateTableInstances(candidates, isTableTemplate);
		if (result.consolidations.length === 0) continue;
		const consolidation = result.consolidations[0];
		const byId = new Map(result.fieldInstances.map((instance) => [instance.id, instance]));
		const newSurvivor = byId.get(survivor.id) as FieldInstance;
		const loserCellIds = new Set(loserCells.map((cell) => cell.id));
		const repointedCells = result.fieldInstances.filter(
			(instance) => loserCellIds.has(instance.id) && instance.tableField === survivor.id,
		) as FieldInstance[];

		try {
			if (repointedCells.length > 0) {
				const cellsToWrite: FieldInstanceForResolver[] = repointedCells.map((cell) => {
					// Full put: everything not copied is erased, so keep the image and alt links explicitly.
					const cellToWrite = createFieldForBatchResolver(cell);
					if (cell.imageId) cellToWrite.imageId = cell.imageId;
					if (cell.altInstanceId) cellToWrite.altInstanceId = cell.altInstanceId;
					if (!online) cellToWrite.touched = 1;
					return cellToWrite;
				});
				if (online) await handleBatchCrud(cellsToWrite, batchCreateFieldInstances, 25);
				else await bulkSaveFieldInstances(cellsToWrite);
			}
			await updateTable({ ...survivor, fieldsOrder: newSurvivor.fieldsOrder, authors: newSurvivor.authors ?? survivor.authors }, online);
			for (const loser of losers) {
				if ((loser.fieldsOrder ?? []).length > 0) await updateTable({ ...loser, fieldsOrder: [] }, online);
			}
		} catch (error) {
			recordSentry({
				error,
				msg: "Open-time consolidation failed part way; the next open or sync will finish it",
				additionalData: { formGroupId, formInstanceId: formInstance.id, ...consolidation, online },
			});
			continue;
		}

		// Reflect the result in the loaded form so this render shows every row and the LINK repair loop sees the cells
		// on the survivor (and nothing on the losers).
		survivor.fieldsOrder = newSurvivor.fieldsOrder;
		survivor.authors = newSurvivor.authors ?? survivor.authors;
		survivor.tableFields = { items: [...(survivor.tableFields?.items ?? []), ...repointedCells] };
		for (const loser of losers) {
			loser.fieldsOrder = [];
			loser.tableFields = { items: [] };
		}

		recordRepairBreadcrumb("open-consolidation", { formGroupId, formInstanceId: formInstance.id, ...consolidation, online });
		done.push(consolidation);
	}
	return done;
}

export default consolidateTableInstancesOnOpen;
